/**
 * store/modules/admin/adminAsset.js
 *
 * Vuex module handling admin-asset state and actions.
 */
import axios from 'axios'

export default {
  // admin-asset state.
  state: {
    adminAssetList: [],
    adminAvailableConsignors: [],
    adminAsset: {},
    adminAssetOwnership: [],
    adminAssetHistory: {},
    nonAdminAssetHistory: {},
  },

  // Mutations to alter admin-asset state.
  mutations: {
    setAssetList: (state, assetList) => (state.adminAssetList = assetList),
    setConsignorList: (state, consignorList) => (state.adminAvailableConsignors = consignorList),
    setAdminAsset: (state, asset) => (state.adminAsset = asset),
    setAdminAssetOwnership: (state, assetOwnership) => (state.adminAssetOwnership = assetOwnership),
    setAdminAssetHistory: (state, assetHistory) => (state.adminAssetHistory = assetHistory),
    setNonAdminAssetHistory: (state, nonAdminAssetHistory) => (state.nonAdminAssetHistory = nonAdminAssetHistory)
  },

  // Actions handling requests, and committing mutations.
  actions: {
    // Grab the asset list.
    async getAssets ({ commit }) {
      try {
        let response = await axios.get('/api/admin/asset')
        // Asset list fetch success, set to state.
        commit('setAssetList', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch assets.')
      }
    },

    // Grab asset by ID.
    async getAssetById ({ commit }, assetId) {
      try {
        let response = await axios.get(`/api/flip-ui/asset-details/${assetId}`)
        // Asset list fetch success, set to state.
        commit('setAdminAsset', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error(`Unable to fetch asset: ${assetId}`)
      }
    },

    // Get available consignors.
    async getAvailableConsignors ({ commit }) {
      try {
        let response = await axios.get('/api/asset/consignors')
        // Asset list fetch success, set to state.
        commit('setConsignorList', response.data)
      }
      catch {
        // Asset list fetch failed.
        console.error('Unable to fetch consignors.')
      }
    },

    // Clear the single asset state.
    clearAdminAsset ({ commit }) {
      commit('setAdminAsset', {})
    },

    // Create an asset.
    async createAsset ({ commit, dispatch }, asset) {
      try {
        let response = await axios.post('/api/admin/flip-ui/asset', asset)
        // Asset creation success, set to state and update list.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        // Asset list fetch failed.
        console.error('Unable to create asset.')
        throw(err)
      }
    },

    // Update an asset.
    async updateAsset ({ commit, dispatch }, asset) {
      try {
        let response = await axios.put(`/api/admin/flip-ui/asset/${asset.id}`, asset)
        // Asset update success, set to state and update list.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        // Asset list fetch failed.
        console.error(`Unable to update asset: ${asset.id}`)
        throw(err)
      }
    },

    /**
     * Update Preferred Purchase Source
     *
     * sourceDetails: {
     *    purchaseSource: Integer (0 - BCC, 1 - Consignor),
     *    assetId: Integer
     * }
     *
     */
    async updatePurchaseSource ({ commit, dispatch }, sourceDetails) {
      try {
        let response = await axios.put(
          `/api/admin/flip-ui/asset/${sourceDetails.assetId}/purchaseSource`,
          { preferredPurchaseSource: sourceDetails.purchaseSource }
        )

        // Asset update success, set to state and update list.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error('Unable to alter purchase source.')
        throw(err)
      }
    },

    /**
     * Begin Sale
     *
     * saleDetails: {
     *  "totalShareCount": 0,
     *  "totalSharePrice": 0
     * }
     **/
    async beginSale ({ commit, dispatch }, { assetId, saleDetails }) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/beginSale`, saleDetails)

        // Begin Sale success.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error('Unable to begin sale.')
        throw(err)
      }
    },

    /**
     * Suspend Sale
     */
    async suspendSales ({ commit, dispatch }, assetId) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/suspend`)

        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to suspend asset ${assetId}`)
      }
    },

    /**
     * Resume Sale
     */
    async resumeSales ({ commit, dispatch }, assetId) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/resume`)

        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to resume asset ${assetId}`)
      }
    },

    /**
     * Purchase (Reconciliation)
     *
     * purchasePayload: {
     *  "totalPurchasePrice": 0,
     *  "userId": 0
     * }
     **/
    async purchase ({ commit, dispatch }, { assetId, purchasePayload }) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/purchase`, purchasePayload)

        // Begin Sale success.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to purchse asset ${assetId}.`)
        console.error(err)
      }
    },

    /**
     * Purchase Shares (admins)
     *
     * purchasePayload: {
     *  "assetId": 0,
     *  "numShares": 0
     * }
     **/
    async adminPurchaseShares ({ commit, dispatch }, purchasePayload) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${purchasePayload.assetId}/purchase-shares`, { numberOfSharesToPurchase: purchasePayload.numShares })

        // Begin Sale success.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to purchse shares for asset ${purchasePayload.assetId}.`)
        throw(err)
      }
    },

    // Cancel Buy it Now  (Reconciliation)
    async buyItNowCancel ({ commit, dispatch }, assetId) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/buyItNowCancel`)

        // Cancel success.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to cancel buy it now of asset ${assetId}.`)
        console.error(err)
      }
    },

    // Confirm Buy it Now  (Reconciliation)
    async buyItNowConfirm ({ commit, dispatch }, assetId) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/buyItNowConfirm`)

        // Cancel success.
        commit('setAdminAsset', response.data)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to confirm buy it now of asset ${assetId}.`)
        console.error(err)
      }
    },

    /**
     * External Purchase
     *
     * assetId: long
     * purchase: {
     *   chargeTransferFee: bool (true),
     *   shares: int,
     *   userId: int
     * }
     */
    async externalPurchase({ commit, dispatch }, {assetId, purchase}) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/externalPurchase`, purchase)

        // External Purchase success refresh asset state.
        commit('setAdminAsset', response)
        dispatch('getAssets')
      }
      catch (err) {
        console.error(`Unable to process external purchase for ${assetId}`)
        console.error(err)
      }
    },


    /**
     * Transfer Shares
     *
     * assetId: long
     * purchase: {
     *   chargeTransferFee: bool (true),
     *   shares: int,
     *   fromUserId: int,
     *   toUserId: int,
     *   pricePerShare: int,
     *   externalTransfer: boolean
     * }
     */
    async transferShares({ commit, dispatch }, {assetId, purchase}) {
      try {
        let response = await axios.post(`/api/admin/flip-ui/asset/${assetId}/transferShares`, purchase)

        // External Purchase success refresh asset state.
        commit('setAdminAsset', response)
        dispatch('getAssets')
      } catch (err) {
        console.error(`Unable to process transfer for ${assetId}`)
        console.error(err)
      }
    },


    // Attach a file to a given asset.
    async attachFile ({ commit }, { assetId, attributeName, fileType, file }) {
      try {
        let response = await axios.post(`/api/asset/${assetId}/file/${attributeName}`, file, {
          headers: {
            'content-type': fileType
          }
        })
        commit('setAdminAsset', response.data)
      }
      catch (err) {
        console.error('Unable to attach File.')
        throw(err)
      }
    },

    // Attach an image to the given asset.
    // type: 'mainImage', 'gallery', etc
    async attachAssetImage ({ commit }, { assetId, type, file }) {
      // Arrange the file as form data.
      let fileFormData = new FormData()
      fileFormData.append('file', file)

      try {
        let response = await axios.post(`/api/asset/${assetId}/image/${type}`, fileFormData, {
          headers: {
            'content-type': 'multipart/form-data'
          }
        })

        commit('setAdminAsset', response.data)
      }
      catch (err) {
        console.error('Unable to attach image.')
        throw(err)
      }
    },

    // Delete a given image for a given asset.
    async deleteAssetImage ({ commit }, { assetId, assetImageId }) {
      try {
        let response = await axios.delete(`/api/asset/${assetId}/image/${assetImageId}`)

        commit('setAdminAsset', response.data)
      }
      catch (err) {
        console.error(err)
      }
    },

    // Get ownership of a given asset.
    async getAssetOwnershipByAssetId ({ commit }, assetId) {
      try {
        let response = await axios.get(`/api/asset/${assetId}/ownership`)

        commit('setAdminAssetOwnership', response.data)
      }
      catch (err) {
        console.error(`Unable to fetch ownership for ${assetId}.`)
        console.error(err)
      }
    },

    /**
     * Update the ownership of an asset.
     *
     * owner: {
     *   forSale: true,
     *   fromUserId: 0,
     *   notes: '',
     *   pricePerShare: 0,
     *   shares: 0,
     *   toUserId: 0
     * }
     */
    async manualAssetOwnershipAdjustment ({ commit }, {assetId, owner}) {
      try {
        let response = await axios.post(`/api/asset/${assetId}/ownership`, owner)
        commit('setAdminAssetOwnership', response.data)
      }
      catch (err) {
        console.error(`Unable to add ownership to ${assetId}`)
        console.error(err)
      }
    },

    async getPurchaseHistoryForAsset ({ commit }, assetId) {
      try {
        let response = await axios.get(`/api/admin/flip-ui/asset/${assetId}/purchaseHistory`)
        commit('setAdminAssetHistory', response.data)
      }
      catch (err) {
        console.error(`Unable to fetch asset history for asset ${assetId}`)
        console.error(err)
      }
    },

    async getNonAdminPurchaseHistoryForAsset ({ commit }, assetId) {
      try {
        let response = await axios.get(`/api/flip-ui/assetPurchaseHistory/${assetId}`)
        commit('setNonAdminAssetHistory', response.data)
      }
      catch (err) {
        console.error(`Unable to fetch non admin asset history for asset ${assetId}`)
        console.error(err)
      }
    },

    clearPurchaseHistoryForAsset ({ commit }) {
      commit('setAdminAssetHistory', {})
    }
  },

  // User state getters.
  getters: {
    adminAssetList: state => state.adminAssetList,
    adminAvailableConsignors: state => state.adminAvailableConsignors,
    adminAsset: state => state.adminAsset,
    adminAssetOwnership: state => state.adminAssetOwnership,
    adminAssetHistory: state => state.adminAssetHistory,
    nonAdminAssetHistory: state => state.nonAdminAssetHistory
  }
}
